/**
 * D-EZ-102 — VICTORY LANE.
 *
 * The winner's moment, before the result sheet: his car, his shop, the name on
 * the hood, how he won it, what it meant, the microphone, and the headline the
 * paper will run. Every word comes from `story/victoryLane.ts`, which reads
 * only the retained race, the books and the league's own history.
 */

import { useMemo } from 'react';
import type { RaceResult } from '../../domain/race.ts';
import type { Team } from '../../domain/field.ts';
import type { FinanceState } from '../../domain/finance/raceFinance.ts';
import type { Milestone } from '../../domain/history/index.ts';
import type { RuleEvent } from '../../domain/ruleEvent.ts';
import { liveryForDriver } from '../../domain/livery.ts';
import { victoryLane } from '../../domain/story/victoryLane.ts';
import { leagueSeasons, victoryContext, type LeagueShelf } from '../../domain/story/league.ts';
import { sponsorSlotsFor } from '../../domain/finance/sponsorNames.ts';
import { emptyPeopleContext, peopleContext, quote, STATE_WORDS, victoryQuotes, type VictoryQuotes } from '../../domain/people/index.ts';
import { MASTHEAD } from '../../domain/paper/fallbackPaper.ts';
import { logoForTeam, useLogos } from '../../lib/logoStore.ts';
import CarTopDown from '../CarTopDown.tsx';

export interface VictoryLaneProps {
  result: RaceResult;
  teams?: readonly Team[];
  /** The books after this race — the sponsor on the hood. */
  contracts?: FinanceState | null;
  /** Milestones the league recorded in this race. */
  milestones?: readonly Milestone[];
  /** This race's slice of the season ledger (the interview). */
  seasonEvents?: readonly RuleEvent[];
  defendingWinnerId?: string | null;
  /**
   * The league, for a season race: milestones, the defending winner and the
   * interview are read from it (`victoryContext`). Overrides the three above.
   */
  league?: { shelf: LeagueShelf; raceNumber: number } | null;
  /** Open the full edition of the paper, when there is one to open. */
  onReadPaper?: () => void;
}

export default function VictoryLane({
  result,
  teams,
  contracts,
  milestones,
  seasonEvents,
  defendingWinnerId,
  league,
  onReadPaper,
}: VictoryLaneProps) {
  const fromLeague = useMemo(() => (league ? victoryContext(league.shelf, league.raceNumber) : null), [league]);
  const model = useMemo(
    () =>
      victoryLane({
        result,
        teams,
        contracts,
        milestones: fromLeague?.milestones ?? milestones,
        seasonEvents: fromLeague?.seasonEvents ?? seasonEvents,
        defendingWinnerId: fromLeague?.defendingWinnerId ?? defendingWinnerId,
      }),
    [result, teams, contracts, milestones, seasonEvents, defendingWinnerId, fromLeague],
  );
  const team = teams?.find((t) => model && t.driverIds.includes(model.winnerId));
  const seat = team && model ? team.driverIds.indexOf(model.winnerId) : undefined;
  const card = model ? [...result.state.grid, ...result.state.retired].find((c) => c.card.driverId === model.winnerId)?.card : undefined;
  const hood = logoForTeam(useLogos(), model?.teamName ?? null);
  /*
    D-EZ-107 / D-EZ-108 — the winner's words and one rival's, in voice. Read
    from the league as it stood at this flag (nothing later is known); an
    exhibition has no league, so only the winner speaks.
  */
  const voices = useMemo((): VictoryQuotes | null => {
    const winnerId = result.winnerId;
    if (!winnerId) return null;
    const sponsorOf = (id: string): string | null => {
      const dfs = contracts?.byDriver[id];
      return dfs ? (sponsorSlotsFor(dfs).find((s) => s.slot === 'Primary')?.name ?? null) : null;
    };
    const teamOf = (id: string): string | null => teams?.find((t) => t.driverIds.includes(id))?.name ?? null;
    try {
      const live = league?.shelf.live;
      if (league && live) {
        const shelf = { ...league.shelf, live: { ...live, state: { ...live.state, races: live.state.races.filter((r) => r.raceNumber <= league.raceNumber) } } };
        const ctx = peopleContext({ archive: leagueSeasons(shelf), throughRace: { seasonNumber: live.seasonNumber, raceNumber: league.raceNumber } });
        return victoryQuotes(ctx, live.seasonNumber, league.raceNumber, { sponsorOf, teamOf });
      }
      const raceId = result.state.raceId ?? 'exhibition';
      const ctx = emptyPeopleContext(raceId);
      return { winner: quote({ kind: 'won', raceId, sponsor: sponsorOf(winnerId), team: teamOf(winnerId) }, winnerId, ctx), other: null };
    } catch {
      return null;
    }
  }, [result, league, contracts, teams]);
  const nameIn = (id: string) => result.finishingOrder.find((f) => f.driverId === id)?.name ?? id;
  if (!model) return null;

  return (
    <section className="vl" aria-labelledby="vl-name">
      <div className="vl__hero">
        <p className="vl__kicker">Victory Lane · {model.raceName}</p>
        <div className="vl__body">
          <div className="vl__car">
            <CarTopDown
              livery={liveryForDriver(model.winnerId, team?.teamId ?? card?.teamId, seat, team?.baseInk as never, card?.make)}
              carNumber={model.carNumber}
              size={200}
              hoodLogoUrl={hood?.url ?? null}
              hoodLogoLabel={hood?.label ?? null}
            />
          </div>
          <div className="vl__who">
            <h2 id="vl-name" className="vl__name">
              {model.name}
            </h2>
            <p className="vl__meta">
              {model.carNumber && <span>No. {model.carNumber}</span>}
              {model.teamName ? <span>{model.teamName}</span> : teams && teams.length > 0 && <span>Independent</span>}
              {model.sponsor && <span>{model.sponsor} on the hood</span>}
              {model.hometown && <span>from {model.hometown}</span>}
            </p>
            {model.how && <p className="vl__how">{model.how.text}</p>}
            {model.milestones.length > 0 && (
              <ul className="vl__milestones" aria-label="Milestones">
                {model.milestones.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="vl__press">
        <article className="vl__paper" aria-label="Tomorrow's headline">
          <p className="vl__masthead">{MASTHEAD}</p>
          <h3 className="vl__headline">{model.headline}</h3>
          {model.standfirst && <p className="vl__standfirst">{model.standfirst}</p>}
          {onReadPaper && (
            <button type="button" className="btn btn--quiet" onClick={onReadPaper}>
              Read the paper
            </button>
          )}
        </article>

        {model.interview && (
          <figure className="vl__mic">
            <p className="vl__mickind">
              At the microphone{model.interview.isWinner ? '' : ` · ${model.interview.category}`}
            </p>
            {model.interview.quote ? (
              <blockquote className="vl__quote">
                <p>{model.interview.quote}</p>
              </blockquote>
            ) : (
              <p className="vl__quote">{model.interview.name} spoke to the cameras.</p>
            )}
            <figcaption className="vl__micwho">— {model.interview.name}</figcaption>
            {model.interview.action && (
              <p className="vl__micnext">
                <b>Next race:</b> {model.interview.action}
              </p>
            )}
          </figure>
        )}
      </div>

      {/* D-EZ-107 — the same afternoon in two men's own voices. The printed
          interview above is the chart's result as printed; these are how each
          man says it, written only from facts the race and the rivalry book
          hold. No words are written for a real (2025) driver. */}
      {voices && (voices.winner || voices.other) && (
        <div className="vl__voices" aria-label="In their own words">
          {voices.winner && (
            <figure className="vl__voice">
              <p className="vl__mickind">The winner</p>
              <blockquote className="vl__quote">
                <p>“{voices.winner.text}”</p>
              </blockquote>
              <figcaption className="vl__micwho">— {nameIn(voices.winner.driverId)}</figcaption>
            </figure>
          )}
          {voices.other && (
            <figure className="vl__voice">
              <p className="vl__mickind">
                {voices.other.rivalry ? `His rival · ${STATE_WORDS[voices.other.rivalry.state]}` : 'Runner-up'}
              </p>
              <blockquote className="vl__quote">
                <p>“{voices.other.quote.text}”</p>
              </blockquote>
              <figcaption className="vl__micwho">— {nameIn(voices.other.quote.driverId)}</figcaption>
              {voices.other.rivalry?.worst && (
                <p className="vl__micnext">
                  <b>Between them:</b> {voices.other.rivalry.worst.text}
                </p>
              )}
            </figure>
          )}
        </div>
      )}
    </section>
  );
}
